/**
 * useTabsEnabled — single source of truth for the runtime "Enable Tabs" gate.
 *
 * Fetches GET /api/app-settings on mount and re-polls every 20s so that when
 * an admin flips the flag (from Lifecycle), other open sessions pick it up
 * without a re-login. Exposes `refetch` and `setTabsEnabledLocal` so the admin
 * toggle / "Enable now" button can update optimistically.
 *
 * `tabsEnabled` is null until the first fetch resolves (treated as loading by
 * the gate). On a fetch error the previous value is kept.
 *
 * Sidebar Roles + Enable-Tabs Gate (2026-06-07).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { appSettingsApi } from '@/api/client';

const POLL_MS = 20_000;

export function useTabsEnabled() {
  const [tabsEnabled, setTabsEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const refetch = useCallback(async () => {
    try {
      const res = await appSettingsApi.get();
      if (mounted.current) setTabsEnabled(res.tabs_enabled);
    } catch {
      // Keep the previous value; the gate treats a null (never-loaded) value
      // as "loading" and a stale value as last-known.
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refetch();
    const timer = setInterval(refetch, POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [refetch]);

  return {
    tabsEnabled,
    loading,
    refetch,
    /** Optimistic local update (admin toggle / "Enable now"). */
    setTabsEnabledLocal: setTabsEnabled,
  };
}
